import React, { useState, useEffect, useRef } from 'react';
import { FaFacebook, FaInstagram, FaTelegramPlane, FaWhatsapp } from "react-icons/fa";
import { SiteConfig } from '../models/SiteConfig';
import { connect } from 'react-redux';
import { RootState } from '../models/RootState';
import { getDynamicTextColors } from '../store/authActions';

interface SocialMediaIconsProps {
  siteConfig?: SiteConfig;
  userDetails?: any;
}

const SocialMediaIcons: React.FC<SocialMediaIconsProps> = ({ siteConfig, userDetails }) => {
  // Initialize state at the top level
  const [showWhatsAppMenu, setShowWhatsAppMenu] = useState<boolean>(false);
  const whatsappMenuRef = useRef<HTMLDivElement>(null);

  // Helper function to calculate brightness from hex color
  const getColorBrightness = (color: string): number => {
    if (!color) return 0;
    const hex = color.replace('#', '');
    const rgbHex = hex.length === 8 ? hex.substring(0, 6) : hex.length === 6 ? hex : hex.substring(0, 6);
    const r = parseInt(rgbHex.substring(0, 2), 16);
    const g = parseInt(rgbHex.substring(2, 4), 16);
    const b = parseInt(rgbHex.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000;
  };

  // Helper function to get text color based on background color
  const getTextColorForBackground = (bgColor: string): string => {
    if (!bgColor) return '#FFFFFF';
    const brightness = getColorBrightness(bgColor);
    return brightness > 128 ? '#000000' : '#FFFFFF';
  };

  // Helper function to determine if main background is light or dark
  const isMainBgLightTheme = () => {
    const backgroundColor = siteConfig?.colorConfig?.background;
    if (!backgroundColor) return false;
    return getColorBrightness(backgroundColor) > 128;
  };

  const mainPageTextColor = isMainBgLightTheme() ? '#000000' : '#FFFFFF';
  
  // Get dynamic text color class for menu background (using background-card as it's similar to purple-balance-bg)
  const { backgroundCardTextColorClass } = getDynamicTextColors(userDetails);
  const whatsappMenuTextColorClass = backgroundCardTextColorClass;

  // Close WhatsApp menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (whatsappMenuRef.current && !whatsappMenuRef.current.contains(event.target as Node)) {
        setShowWhatsAppMenu(false);
      }
    };

    if (showWhatsAppMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showWhatsAppMenu]);

  // Don't render if no contact info
  if (!siteConfig?.contactInfo?.WhatsappSignup && 
      !siteConfig?.contactInfo?.WhatsappSupport && 
      !siteConfig?.contactInfo?.Instagram && 
      !siteConfig?.contactInfo?.TelegramChannel &&
      !siteConfig?.contactInfo?.Facebook) {
    return null;
  }

  return (
    <div className="fixed z-40 flex flex-col gap-2 right-3 top-1/2 -translate-y-1/2 md:right-4">
      {/* Instagram */}
      {siteConfig?.contactInfo?.Instagram && (
        <a 
          href={(() => {
            try {
              const raw = siteConfig.contactInfo.Instagram.trim();
              return raw.startsWith("http")
                ? raw
                : `https://instagram.com/${raw}`;
            } catch (err) {
              return "#";
            }
          })()}
          target="_blank" 
          rel="noopener noreferrer" 
          className="w-9 h-9 md:w-10 md:h-10 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
          style={{ background: 'linear-gradient(to bottom right, var(--instagram-gradient-start), var(--instagram-gradient-mid), var(--instagram-gradient-end))' }}
          onClick={(e) => {
            try {
              const raw = siteConfig.contactInfo.Instagram.trim();
              const link = raw.startsWith("http")
                ? raw
                : `https://instagram.com/${raw}`;
              if (window.Telegram?.WebApp?.openLink) {
                e.preventDefault();
                window.Telegram.WebApp.openLink(link);
              }
            } catch (err) {
              console.warn("Instagram link blocked or invalid:", err);
            }
          }}
        >
          <FaInstagram size={20} style={{ color: mainPageTextColor }} />
        </a>
      )}

      {/* WhatsApp - Show popup if both exist, otherwise single button */}
      {(siteConfig?.contactInfo?.WhatsappSignup && siteConfig?.contactInfo?.WhatsappSupport) ? (
        <div className="relative" ref={whatsappMenuRef}>
          <button
            onClick={() => setShowWhatsAppMenu(!showWhatsAppMenu)}
            className="w-9 h-9 md:w-10 md:h-10 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
            style={{ backgroundColor: 'var(--whatsapp-color)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--whatsapp-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--whatsapp-color)'; }}
          >
            <FaWhatsapp size={20} style={{ color: 'var(--text-white)' }} />
          </button>
          {showWhatsAppMenu && (
            <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 rounded-2xl shadow-lg p-2 min-w-[180px] z-[60]" style={{ backgroundColor: 'var(--purple-balance-bg)', borderColor: 'var(--purple-accent)', borderWidth: '1px', borderStyle: 'solid' }}>
              <a
                href={(() => {
                  try {
                    const raw = siteConfig.contactInfo.WhatsappSupport!.trim();
                    return raw.startsWith("http")
                      ? raw
                      : `https://wa.me/${raw.replace(/[^0-9]/g, "")}`;
                  } catch (err) {
                    return "#";
                  }
                })()}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${whatsappMenuTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--overlay-white-10)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                onClick={(e) => {
                  try {
                    const raw = siteConfig.contactInfo.WhatsappSupport!.trim();
                    const link = raw.startsWith("http")
                      ? raw
                      : `https://wa.me/${raw.replace(/[^0-9]/g, "")}`;
                    if (window.Telegram?.WebApp?.openLink) {
                      e.preventDefault();
                      window.Telegram.WebApp.openLink(link);
                    }
                  } catch (err) {
                    console.warn("WhatsApp link blocked or invalid:", err);
                  }
                  setShowWhatsAppMenu(false);
                }}
              >
                <FaWhatsapp size={18} style={{ color: 'var(--whatsapp-color)' }} />
                <span className={`font-medium text-sm ${whatsappMenuTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>Support</span>
              </a>
              <a
                href={(() => {
                  try {
                    const raw = siteConfig.contactInfo.WhatsappSignup!.trim();
                    return raw.startsWith("http")
                      ? raw
                      : `https://wa.me/${raw.replace(/[^0-9]/g, "")}`;
                  } catch (err) {
                    return "#";
                  }
                })()}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${whatsappMenuTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--overlay-white-10)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                onClick={(e) => {
                  try {
                    const raw = siteConfig.contactInfo.WhatsappSignup!.trim();
                    const link = raw.startsWith("http")
                      ? raw
                      : `https://wa.me/${raw.replace(/[^0-9]/g, "")}`;
                    if (window.Telegram?.WebApp?.openLink) {
                      e.preventDefault();
                      window.Telegram.WebApp.openLink(link);
                    }
                  } catch (err) {
                    console.warn("WhatsApp link blocked or invalid:", err);
                  }
                  setShowWhatsAppMenu(false);
                }}
              >
                <FaWhatsapp size={18} style={{ color: 'var(--whatsapp-color)' }} />
                <span className={`font-medium text-sm ${whatsappMenuTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>Get Your ID</span>
              </a>
            </div>
          )}
        </div>
      ) : siteConfig?.contactInfo?.WhatsappSignup ? (
        <a 
          href={(() => {
            try {
              const raw = siteConfig.contactInfo.WhatsappSignup.trim();
              return raw.startsWith("http")
                ? raw
                : `https://wa.me/${raw.replace(/[^0-9]/g, "")}`;
            } catch (err) {
              return "#";
            }
          })()}
          target="_blank" 
          rel="noopener noreferrer" 
          className="w-9 h-9 md:w-10 md:h-10 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
          style={{ backgroundColor: 'var(--whatsapp-color)' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--whatsapp-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--whatsapp-color)'; }}
          onClick={(e) => {
            try {
              const raw = siteConfig.contactInfo.WhatsappSignup.trim();
              const link = raw.startsWith("http")
                ? raw
                : `https://wa.me/${raw.replace(/[^0-9]/g, "")}`;
              if (window.Telegram?.WebApp?.openLink) {
                e.preventDefault();
                window.Telegram.WebApp.openLink(link);
              }
            } catch (err) {
              console.warn("WhatsApp link blocked or invalid:", err);
            }
          }}
        >
          <FaWhatsapp size={20} style={{ color: 'var(--text-white)' }} />
        </a>
      ) : siteConfig?.contactInfo?.WhatsappSupport ? (
        <a 
          href={(() => {
            try {
              const raw = siteConfig.contactInfo.WhatsappSupport.trim();
              return raw.startsWith("http")
                ? raw
                : `https://wa.me/${raw.replace(/[^0-9]/g, "")}`;
            } catch (err) {
              return "#";
            }
          })()}
          target="_blank" 
          rel="noopener noreferrer" 
          className="w-9 h-9 md:w-10 md:h-10 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
          style={{ backgroundColor: 'var(--whatsapp-color)' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--whatsapp-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--whatsapp-color)'; }}
          onClick={(e) => {
            try {
              const raw = siteConfig.contactInfo.WhatsappSupport.trim();
              const link = raw.startsWith("http")
                ? raw
                : `https://wa.me/${raw.replace(/[^0-9]/g, "")}`;
              if (window.Telegram?.WebApp?.openLink) {
                e.preventDefault();
                window.Telegram.WebApp.openLink(link);
              }
            } catch (err) {
              console.warn("WhatsApp link blocked or invalid:", err);
            }
          }}
        >
          <FaWhatsapp size={20} style={{ color: 'var(--text-white)' }} />
        </a>
      ) : null}

      {/* Telegram */}
      {siteConfig?.contactInfo?.TelegramChannel && (
        <a 
          href={(() => {
            try {
              const tg = siteConfig.contactInfo.TelegramChannel.trim();
              let link = tg;
              if (!tg.startsWith('t.me') && !tg.startsWith('http')) {
                link = `t.me/${tg}`;
              }
              return link.startsWith('http') ? link : `//${link}`;
            } catch (err) {
              return "#";
            }
          })()}
          target="_blank" 
          rel="noopener noreferrer" 
          className="w-9 h-9 md:w-10 md:h-10 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
          style={{ backgroundColor: 'var(--telegram-color)' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--telegram-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--telegram-color)'; }}
          onClick={(e) => {
            try {
              const tg = siteConfig.contactInfo.TelegramChannel.trim();
              let link = tg;
              if (!tg.startsWith('t.me') && !tg.startsWith('http')) {
                link = `t.me/${tg}`;
              }
              const finalLink = link.startsWith('http') ? link : `//${link}`;
              if (window.Telegram?.WebApp?.openLink) {
                e.preventDefault();
                window.Telegram.WebApp.openLink(finalLink.startsWith('//') ? `https:${finalLink}` : finalLink);
              } else {
                window.open(finalLink, '_blank');
              }
            } catch (err) {
              console.warn("Telegram link blocked or invalid:", err);
            }
          }}
        >
          <FaTelegramPlane size={20} style={{ color: 'var(--text-white)' }} />
        </a>
      )}

      {/* Facebook */}
      {siteConfig?.contactInfo?.Facebook && (
        <a 
          href={(() => {
            try {
              const raw = siteConfig.contactInfo.Facebook.trim();
              return raw.startsWith("http")
                ? raw
                : `https://facebook.com/${raw}`;
            } catch (err) {
              return "#";
            }
          })()}
          target="_blank" 
          rel="noopener noreferrer" 
          className="w-9 h-9 md:w-10 md:h-10 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
          style={{ backgroundColor: 'var(--telegram-color)' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--telegram-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--telegram-color)'; }}
          onClick={(e) => {
            try {
              const raw = siteConfig.contactInfo.Facebook.trim();
              const link = raw.startsWith("http")
                ? raw
                : `https://facebook.com/${raw}`;
              if (window.Telegram?.WebApp?.openLink) {
                e.preventDefault();
                window.Telegram.WebApp.openLink(link);
              }
            } catch (err) {
              console.warn("Facebook link blocked or invalid:", err);
            }
          }}
        >
          <FaFacebook size={20} style={{ color: 'var(--text-white)' }} />
        </a>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    userDetails: state.userDetails?.userDetails,
  };
};

export default connect(mapStateToProps)(SocialMediaIcons);
